import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import ProjectCard3D from "../cards/ProjectCard3D";
import { projects } from "../../data/projects";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  padding: 80px 24px 60px;

  @media (max-width: 768px) {
    padding: 60px 16px 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 12px;
  letter-spacing: -0.5px;

  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  text-align: center;
  max-width: 620px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.7;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    font-size: 14.5px;
    margin-bottom: 24px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 40px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  padding: 5px;
  border-radius: 999px;

  @media (max-width: 640px) {
    border-radius: 14px;
    padding: 6px;
    width: 100%;
  }
`;

const FilterButton = styled.button`
  position: relative;
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ active, theme }) => (active ? "#030712" : theme.text_secondary)};
  transition: color 0.2s ease;
  z-index: 1;

  &:hover {
    color: ${({ active, theme }) => (active ? "#030712" : theme.text_primary)};
  }

  @media (max-width: 640px) {
    flex: 1;
    min-width: 100px;
    text-align: center;
  }
`;

const ActiveFilterPill = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, #38bdf8 100%);
  border-radius: 999px;
  z-index: -1;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  width: 100%;
  justify-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 22px;
  }
`;

const Projects = ({ openModal, setOpenModal }) => {
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    "all",
    ...Array.from(new Set(projects.map((p) => p.category)))
  ];

  const filteredProjects =
    activeCategory === "all"
      ? projects
      : projects.filter((p) => p.category === activeCategory);

  const handleOpenModal = (project) => {
    if (setOpenModal) {
      setOpenModal({ state: true, project });
    }
  };

  return (
    <Section id="projects">
      <ContentWrapper>
        <Title
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Featured <span>Projects</span>
        </Title>

        <Description
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Selected web applications and full-stack platforms built with React.js, Node.js, Express, MongoDB, and modern cloud tools.
        </Description>

        <FilterContainer>
          {categories.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <FilterButton
                key={cat}
                active={isActive}
                onClick={() => setActiveCategory(cat)}
              >
                {isActive && (
                  <ActiveFilterPill
                    layoutId="activeProjectFilterPill"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                {cat === "all" ? "All Works" : cat}
              </FilterButton>
            );
          })}
        </FilterContainer>

        <Grid>
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <ProjectCard3D
                key={project.id}
                project={project}
                onOpenModal={handleOpenModal}
              />
            ))}
          </AnimatePresence>
        </Grid>
      </ContentWrapper>
    </Section>
  );
};

export default Projects;
